'use client'

import { useState, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { Box, Container } from '@mui/material'
import { useForm, useWatch } from 'react-hook-form'
import { enqueueSnackbar } from 'notistack'
import JalaliX from '@jalalix/typescript'

// Types
import type { FieldValues } from 'react-hook-form'
import type { APIUsernameLoginRequest, APIUsernameLoginResult } from '@/types/api/(user)/username/login'
import type { LoginProps } from '@/types/components/pages/main/login'

// Configurations
import Config from '@/config'
import APIConfig from '@/config/api'

// Helpers
import Cookies from '@/helpers/cookies'
import { useUserStore } from '@/helpers/store/user'

// Utils
import URLParams from '@/utils/api/url-params'

// Components
const UsernameFormOrganism = dynamic(() => import('@/components/organisms/forms/main/login/username'))

const LoginUsernamePage = (props: LoginProps) => {
	// Props
	const { lng, locale } = props

	// Variables
	const router = useRouter()
	const { setIsLogin, setLastCheckLoginDate, setUUID, setUser_Name, setUsername, setEmail, setPicture, setGroup } = useUserStore()
	const [loading, setLoading] = useState<boolean>(false)

	// Form
	const usernameForm = useForm<FieldValues>({
		mode: 'onChange',
		defaultValues: {
			username: '',
			password: ''
		}
	})

	const usernameValue: string = useWatch({ control: usernameForm.control, name: 'username' })
	const passwordValue: string = useWatch({ control: usernameForm.control, name: 'password' })
	const turnstileTokenValue: string = useWatch({ control: usernameForm.control, name: 'turnstile_token' })

	// Callbacks
	const loginRequest = useCallback(
		async (props: APIUsernameLoginRequest) => {
			// Loading
			setLoading(true)

			// Request
			const res = await fetch(`${APIConfig.appUrl}/user/username/login?${URLParams(props)}`, {
				method: 'POST',
				headers: { 'Accept-Language': lng }
			})

			// Result
			const data: APIUsernameLoginResult = await res.json()

			// Check Result
			if (data.status) {
				setIsLogin(true)

				if (data.data?.item) {
					const user = data.data.item
					setLastCheckLoginDate(new JalaliX().toDateString())

					if (user) {
						if (user.uuid) setUUID(user.uuid)

						setUser_Name(user.user_name)
						setPicture(user.picture)

						if (user.username) setUsername(user.username)
						if (user.email) setEmail(user.email)
						if (user.group) setGroup(user.group)
					}
				}

				// Set Cookie
				if (data.data?.tokenValue) {
					const isSecureContext = typeof window !== 'undefined' && ['https:'].includes(window.location.protocol)

					Cookies.set(`${Config.shortName}-token`, data.data.tokenValue, {
						path: '/',
						secure: isSecureContext,
						sameSite: 'lax',
						expires: new Date(data.data.token.expires_date ?? Date.now() + 86400000 * 7)
					})
				}

				router.push(`/${lng}`)
			} else setLoading(false)

			// Check Message
			if (data.message) enqueueSnackbar(data.message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 })
			if (data.messages) data.messages.map(message => enqueueSnackbar(message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 }))
		},
		[lng, router, setIsLogin, setLastCheckLoginDate, setUUID, setUser_Name, setUsername, setEmail, setPicture, setGroup]
	)

	return (
		<Container maxWidth="xs" sx={{ height: '100dvh' }}>
			<Box
				sx={{
					display: 'inline-flex',
					width: 1,
					height: 1
				}}
			>
				<UsernameFormOrganism
					lng={lng}
					locale={locale}
					control={usernameForm.control}
					formState={usernameForm.formState}
					loading={loading}
					setValue={usernameForm.setValue}
					onConfirm={() => loginRequest({ username: usernameValue, password: passwordValue, turnstile_token: turnstileTokenValue })}
				/>
			</Box>
		</Container>
	)
}

export default LoginUsernamePage
